"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import styled from "@emotion/styled"
import { breakpoints } from "../../theme/mediaQueries"

/* Every strip used to pin itself with `position: fixed; bottom: 0` at the same
   z-index, so two strips necessarily occupied the same corner of the screen. The
   strips now share ONE fixed container, and only one of them is ever mounted —
   see useHeroStripSlot below. */
const Stack = styled.div`
  position: fixed;
  bottom: 0;
  inset-inline: 0;
  z-index: 1002;

  @media (min-width: ${breakpoints.lg}px) {
    z-index: 1000;
  }
`

interface SlotApi {
  claim: (id: string, order: number) => void
  release: (id: string) => void
  activeId: string | null
}

const SlotContext = createContext<SlotApi | null>(null)

/**
 * A page may hold several hero strips, but they show one at a time: the earliest
 * one in CMS order that is still eligible, and the next one only once that has
 * been dismissed. Each strip resolves its own gates (isActive, expiry date, this
 * session's dismissal, the homepage splash) and reports the result as `ready`;
 * whichever ready strip sits lowest in CMS order wins the slot.
 *
 * Registration order can't be trusted — the strips arrive from separate lazy
 * chunks — so the CMS position is passed in rather than inferred.
 *
 * Outside a stack (a strip rendered on its own) there is nothing to arbitrate,
 * so `ready` passes straight through.
 */
export function useHeroStripSlot(id: string, order: number, ready: boolean) {
  const api = useContext(SlotContext)
  const { claim, release } = api ?? {}

  useEffect(() => {
    if (!claim || !release || !ready) return
    claim(id, order)
    return () => release(id)
  }, [claim, release, id, order, ready])

  if (!api) return ready
  return api.activeId === id
}

export default function HeroStripStack({ children }: { children: ReactNode }) {
  const [claimants, setClaimants] = useState<Record<string, number>>({})

  const claim = useCallback((id: string, order: number) => {
    setClaimants((prev) =>
      prev[id] === order ? prev : { ...prev, [id]: order },
    )
  }, [])

  const release = useCallback((id: string) => {
    setClaimants((prev) => {
      if (!(id in prev)) return prev
      const next = { ...prev }
      delete next[id]
      return next
    })
  }, [])

  const activeId = useMemo(() => {
    const entries = Object.entries(claimants)
    if (entries.length === 0) return null
    return entries.reduce((best, entry) =>
      entry[1] < best[1] ? entry : best,
    )[0]
  }, [claimants])

  const api = useMemo<SlotApi>(
    () => ({ claim, release, activeId }),
    [claim, release, activeId],
  )

  return (
    <SlotContext.Provider value={api}>
      <Stack data-hero-strip-stack="">{children}</Stack>
    </SlotContext.Provider>
  )
}
